import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeProvider";
import { BrandProvider } from "@/context/BrandProvider";
import { I18nProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthProvider";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import StorePreview from "@/pages/StorePreview";

import AdminLayout from "@/admin/AdminLayout";
import {
  AdminDashboard, AdminUsers, AdminMerchants, AdminPlans, AdminCreditPacks,
  AdminProviders, AdminModels, AdminPrompts, AdminAIRouter, AdminCost, AdminCredits,
  AdminBrand, AdminSEO, AdminLegal, AdminAppearance, AdminBilling, AdminFeatureFlags, AdminAudit, AdminSettings,
} from "@/admin/pages";

import LegalPage from "@/pages/LegalPage";
import { AdminShopify } from "@/admin/AdminShopify";

import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import MyStore from "@/pages/dashboard/MyStore";
import Products from "@/pages/dashboard/Products";
import AISection from "@/pages/dashboard/AISection";
import AICommerceIdentity from "@/pages/dashboard/AICommerceIdentity";
import ImportCatalog from "@/pages/dashboard/ImportCatalog";
import ShopifyConnect from "@/pages/dashboard/ShopifyConnect";
import AIReadiness from "@/pages/dashboard/AIReadiness";
import AIShare from "@/pages/dashboard/AIShare";
import TestStore from "@/pages/dashboard/TestStore";
import Connectors from "@/pages/dashboard/Connectors";
import Roadmap from "@/pages/dashboard/Roadmap";
import Settings from "@/pages/dashboard/Settings";

function FullscreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullscreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth session_id synchronously before any route/auth logic.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/store/:id" element={<StorePreview />} />
      <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
      <Route path="/terms" element={<LegalPage docKey="terms" />} />
      <Route path="/cookie" element={<LegalPage docKey="cookie" />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="credit-packs" element={<AdminCreditPacks />} />
        <Route path="ai-models" element={<AdminModels />} />
        <Route path="prompts" element={<AdminPrompts />} />
        <Route path="ai-providers" element={<AdminProviders />} />
        <Route path="ai-router" element={<AdminAIRouter />} />
        <Route path="cost" element={<AdminCost />} />
        <Route path="credits" element={<AdminCredits />} />
        <Route path="appearance" element={<AdminAppearance />} />
        <Route path="integrations/shopify" element={<AdminShopify />} />
        <Route path="brand" element={<AdminBrand />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="legal" element={<AdminLegal />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="feature-flags" element={<AdminFeatureFlags />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      <Route
        path="/dashboard"
        element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}
      >
        <Route index element={<DashboardHome />} />
        <Route path="store" element={<MyStore />} />
        <Route path="products" element={<Products />} />
        <Route path="import" element={<ImportCatalog />} />
        <Route path="shopify" element={<ShopifyConnect />} />
        <Route path="identity" element={<AICommerceIdentity />} />
        <Route path="ai" element={<AISection />} />
        <Route path="readiness" element={<AIReadiness />} />
        <Route path="share" element={<AIShare />} />
        <Route path="test" element={<TestStore />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="roadmap" element={<Roadmap />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrandProvider>
              <BrowserRouter>
                <AppRouter />
                <Toaster position="top-right" richColors closeButton />
              </BrowserRouter>
            </BrandProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </div>
  );
}
